import type { ReactNode } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import { LayerBoundary } from '../../providers/LayerProvider';
import { cn } from '../../utils/cn';

interface PopoverCaretProps {
	left: number;
	top?: number;
	placement: 'top' | 'bottom' | 'left' | 'right';
	size: number;
	className?: string;
}

function PopoverCaret({
	left,
	top,
	placement,
	size,
	className,
}: PopoverCaretProps) {
	if (placement === 'bottom') {
		// Triangle pointing up (popover below trigger)
		return (
			<div
				className={cn('absolute pointer-events-none', className)}
				style={{
					left,
					top: -size,
					width: 0,
					height: 0,
					borderLeft: `${size}px solid transparent`,
					borderRight: `${size}px solid transparent`,
					borderBottom: `${size}px solid white`,
					filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))',
					zIndex: 10,
				}}
			/>
		);
	}

	if (placement === 'top') {
		// Triangle pointing down (popover above trigger)
		return (
			<div
				className={cn('absolute pointer-events-none', className)}
				style={{
					left,
					bottom: -size / 2,
					width: 0,
					height: 0,
					borderLeft: `${size}px solid transparent`,
					borderRight: `${size}px solid transparent`,
					borderTop: `${size}px solid white`,
					filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
					zIndex: 10,
				}}
			/>
		);
	}

	if (placement === 'right') {
		// Triangle pointing left (popover to right of trigger)
		return (
			<div
				className={cn('absolute pointer-events-none', className)}
				style={{
					left: -size,
					top,
					width: 0,
					height: 0,
					borderTop: `${size}px solid transparent`,
					borderBottom: `${size}px solid transparent`,
					borderRight: `${size}px solid white`,
					filter: 'drop-shadow(-1px 0 1px rgba(0,0,0,0.1))',
					zIndex: 10,
				}}
			/>
		);
	}

	// placement === 'left' - Triangle pointing right (popover to left of trigger)
	return (
		<div
			className={cn('absolute pointer-events-none', className)}
			style={{
				right: -size,
				top,
				width: 0,
				height: 0,
				borderTop: `${size}px solid transparent`,
				borderBottom: `${size}px solid transparent`,
				borderLeft: `${size}px solid white`,
				filter: 'drop-shadow(1px 0 1px rgba(0,0,0,0.1))',
				zIndex: 10,
			}}
		/>
	);
}

interface PopoverProps {
	isOpen: boolean;
	style: React.CSSProperties;
	children: ReactNode;
	showCaret?: boolean;
	caretProps?: {
		left: number;
		top?: number;
		placement: 'top' | 'bottom' | 'left' | 'right';
		size: number;
		visible?: boolean;
	} | null;
	className?: string;
	caretClassName?: string;
	container?: Element;
	usePortal?: boolean;
	contentRef?: React.RefObject<HTMLDivElement | null>;
	/** `usePopover`'s `layerProps`, so it can tell a press inside the panel's
	 * portalled menus from one outside it. */
	onPointerDownCapture?: () => void;
	onKeyDownCapture?: () => void;
}

export function Popover({
	isOpen,
	style,
	children,
	showCaret = true,
	caretProps,
	className,
	caretClassName,
	container = document.body,
	usePortal = true,
	contentRef,
	onPointerDownCapture,
	onKeyDownCapture,
}: PopoverProps) {
	if (!isOpen) {
		return null;
	}

	const content = (
		<div
			ref={contentRef}
			style={style}
			className={cn('popover-content', className)}
			data-popover-content
			onPointerDownCapture={onPointerDownCapture}
			onKeyDownCapture={onKeyDownCapture}
		>
			{showCaret && caretProps && caretProps.visible !== false && (
				<PopoverCaret
					left={caretProps.left}
					top={caretProps.top}
					placement={caretProps.placement}
					size={caretProps.size}
					className={caretClassName}
				/>
			)}

			{/* A portalled panel tells the overlays opened inside it (a select, a
			    tooltip) what it sits at, so they open above it. In place, it lives
			    in its parent's stacking context and its z-index is only local, so
			    the parent's layer already applies and nothing is declared. */}
			{usePortal && typeof style.zIndex === 'number' ? (
				<LayerBoundary zIndex={style.zIndex}>{children}</LayerBoundary>
			) : (
				children
			)}
		</div>
	);

	return usePortal ? createPortal(content, container) : content;
}

export default Popover;
